<script setup lang="ts">
import { HgSelect, type HgSelectOptions } from '@smg-real-estate/domus';
import type { Locale } from '@smg-real-estate/geo-libs';
import { storeToRefs } from 'pinia';
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  isNavigationFailure,
  NavigationFailureType,
  useRoute,
  useRouter,
  type RouteLocationRaw,
} from 'vue-router';

import { sanitizeListingTypeParam } from '@/helpers/String/sanitizeQueryParams';
import { useLocale } from '@/router/routeMeta';
import { useAgencyProfilesStore } from '@/stores/agencyProfiles';
import { trackSortingOptionChange } from '@/tracking/tracking';
import { ListingType, SortingOption } from '@/types';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const locale = useLocale();

const agencyProfilesStore = useAgencyProfilesStore();
const { selectedSortingOption } = storeToRefs(agencyProfilesStore);

// Get listing type value from route to avoid updates of the search options before search model is updated
const listingType = computed(() => sanitizeListingTypeParam(route.query.listingType));

const sortingDropdownOptions = computed((): HgSelectOptions[] => {
  const options: HgSelectOptions[] = [
    { value: SortingOption.DEFAULT, text: t('sortingDropdown.topResults').toString() },
    { value: SortingOption.ALPHABETICALLY, text: t('sortingDropdown.alphabetically').toString() },
  ];

  if (listingType.value !== ListingType.SALE) {
    options.push({
      value: SortingOption.PROPERTIES_FOR_RENT,
      text: t('sortingDropdown.rentPropertyCount.high').toString(),
    });
  }

  if (listingType.value !== ListingType.RENT) {
    options.push({
      value: SortingOption.PROPERTIES_FOR_SALE,
      text: t('sortingDropdown.salePropertyCount.high').toString(),
    });
  }

  return options;
});

watch(selectedSortingOption, async () => {
  router
    .push(
      agencyProfilesStore.nextPageRoute(
        1,
        locale.value as unknown as Locale,
      ) as unknown as RouteLocationRaw,
    )
    .catch((error) => {
      // Ignore duplicated navigation errors
      if (!isNavigationFailure(error, NavigationFailureType.duplicated)) {
        throw Error(error);
      }
    });
});
</script>

<template>
  <HgSelect
    v-model="selectedSortingOption"
    name="sortingDropdown"
    :options="sortingDropdownOptions"
    :class="$style.sortingDropdown"
    @change="trackSortingOptionChange(selectedSortingOption)"
  />
</template>

<style module lang="scss">
@use '@/assets/variables' as *;
</style>
